import PropTypes from 'prop-types';
import {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DONATION_STEPS } from '../components/Home/data';
import { supabase } from '../lib/supabase';
import paths from 'routes/paths';

const donationPathMap = Object.entries(paths.donation).reduce(
  (acc, [step, path]) => {
    if (DONATION_STEPS[step]) {
      acc.stepToPath[DONATION_STEPS[step]] = path;
      acc.pathToStep[path] = DONATION_STEPS[step];
    }
    return acc;
  },
  { stepToPath: {}, pathToStep: {} }
);

export const DonationContext = createContext({
  donationPackages: [],
  setDonationPackages: () => {},
  urgentAppeal: [],
  setUrgentAppeal: () => {},
  giftAid: false,
  setGiftAid: () => {},
  transactionFee: false,
  setTransactionFee: () => {},
  transactionFeeAmount: 0,
  setTransactionFeeAmount: () => {},
  donor: {},
  setDonor: () => {},
  subtotal: 0,
  totalAmount: 0,
  setClientSecret: () => {},
  clientSecret: null,
  setStripeKey: () => {},
  stripeKey: null
});

export function DonationContextProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const organizationId = process.env.REACT_APP_ORGANIZATION_ID;

  const pathname = location.pathname.toLowerCase();
  const matchedEntry = Object.entries(donationPathMap.pathToStep).find(
    ([path]) => pathname.includes(path)
  );
  const [donationStep, _setDonationStep] = useState(
    matchedEntry ? matchedEntry[1] : DONATION_STEPS.BASKET_CREATION
  );

  const setDonationStep = useCallback(
    step => {
      _setDonationStep(step);
      const targetPath =
        donationPathMap.stepToPath[step] || paths.donation.root;
      navigate(targetPath, { replace: true });
    },
    [navigate]
  );

  const [basket, setBasket] = useState(() => {
    const savedBasket = localStorage.getItem('donation_basket');
    return savedBasket ? JSON.parse(savedBasket) : [];
  });

  const [donor, setDonor] = useState(() => {
    const savedDonor = localStorage.getItem('donor');
    return savedDonor ? JSON.parse(savedDonor) : {}
  })

  const [isPackageDrawerOpen, setIsPackageDrawerOpen] = useState(false);
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const [isGiftAidDrawerOpen, setIsGiftAidDrawerOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalSource, setAuthModalSource] = useState('checkout'); // 'checkout' | 'header'
  const [isDonorAuthenticated, setIsDonorAuthenticated] = useState(() => {
    return localStorage.getItem('isDonorAuthenticated') === 'true';
  });
  const [donationPackages, setDonationPackages] = useState([]);
  const [urgentAppeal, setUrgentAppeal] = useState([]);
  const [giftAid, setGiftAid] = useState(false);
  const [transactionFee, setTransactionFee] = useState(false);
  const [transactionFeeAmount, setTransactionFeeAmount] = useState(0);

  const [stripeKey, setStripeKey] = useState(() => {
    const savedKey = localStorage.getItem('stripeKey');
    return savedKey ? savedKey : null
  });

   const [clientSecret, setClientSecret] = useState(() => {
    const savedKey = localStorage.getItem('clientSecret');
    return savedKey ? savedKey : null
  });

  const addDonationToBasket = donation => {
    setBasket(prevState => [...prevState, donation]);
    setIsCartDrawerOpen(true);
  };
  const increaseDonationCount = (donationId, increaseBy) => {
    setBasket(prevState =>
      prevState.map(item => {
        if (item.id === donationId) {
          return { ...item, count: item.count + increaseBy };
        }
        return item;
      })
    );
  };
  const decreaseDonationCount = (donationId, decreaseBy) => {
    const basketItem = basket.find(item => item.id === donationId);
    const newCount = basketItem.count - decreaseBy;

    if (newCount === 0) {
      setBasket(prevState => prevState.filter(item => item.id !== donationId));
    } else {
      setBasket(prevState =>
        prevState.map(item => {
          if (item.id === donationId) {
            return { ...item, count: newCount };
          }
          return item;
        })
      );
    }
  };

  const subtotal = useMemo(
    () =>
      basket.reduce(
        (acc, curr) => acc + curr.count * curr.donationAmount.value,
        0
      ),
    [basket]
  );

  const totalAmount = useMemo(
    () => {
      const total = subtotal + (transactionFee ? transactionFeeAmount : 0);
      return Math.round(total * 100) / 100; // Round to 2 decimal places
    },
    [subtotal, transactionFee, transactionFeeAmount]
  );

  const removeDonation = donationId => {
    setBasket(prevState => prevState.filter(item => item.id !== donationId));
  };

  useEffect(() => {
    localStorage.setItem('donation_basket', JSON.stringify(basket));
    localStorage.setItem('donor', JSON.stringify(donor));
    localStorage.setItem('stripeKey', stripeKey);
    localStorage.setItem('clientSecret', clientSecret);
    localStorage.setItem('isDonorAuthenticated', isDonorAuthenticated);
  }, [basket, donor, stripeKey, clientSecret, isDonorAuthenticated]);

  // The localStorage value above is only a first guess so the header doesn't
  // flicker on load. Supabase holds the real answer, so reconcile against it
  // once the session is known and stay subscribed: a session that expires or is
  // signed out in another tab would otherwise leave the header showing a logged
  // in donor indefinitely.
  useEffect(() => {
    let isActive = true;

    supabase.auth.getSession().then(({ data }) => {
      if (isActive) setIsDonorAuthenticated(!!data?.session);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsDonorAuthenticated(!!session);
    });

    return () => {
      isActive = false;
      subscription?.subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [donationStep]);

  useEffect(() => {
    document.body.style.backgroundColor = '#fff';
  }, []);

  // Handle browser back button navigation
  useEffect(() => {
    const handlePopState = (event) => {
      // Prevent default back navigation and redirect to home
      event.preventDefault();
      
      // Determine where to navigate based on current step
      if (donationStep === DONATION_STEPS.DONOR_INFORMATION) {
        setDonationStep(DONATION_STEPS.BASKET_CREATION);
      } else if (donationStep === DONATION_STEPS.PAYMENT) {
        setDonationStep(DONATION_STEPS.DONOR_INFORMATION);
      } else if (donationStep === DONATION_STEPS.THANKS || donationStep === DONATION_STEPS.FAILED) {
        setDonationStep(DONATION_STEPS.BASKET_CREATION);
      }
    };

    // Push current state to history to enable back button handling
    window.history.pushState({ donationStep }, '', window.location.href);
    
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [donationStep, setDonationStep]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const redirectStatus = searchParams.get('redirect_status');

    // Detect Supabase auth callback (hash contains access_token, type, or error)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const isAuthCallback = hashParams.get('access_token') || hashParams.get('type') || hashParams.get('error');

    if (isAuthCallback) {
      const errorDescription = hashParams.get('error_description');
      if (errorDescription) {
        toast.error(decodeURIComponent(errorDescription.replace(/\+/g, ' ')));
      }
      // Clear the hash and stay on home page
      window.history.replaceState(null, '', window.location.pathname);
      return;
    }

    if (redirectStatus) {
      if (redirectStatus === 'succeeded') {
        setDonationStep(DONATION_STEPS.THANKS);
      } else {
        setDonationStep(DONATION_STEPS.FAILED);
      }
    } else if (
      basket.length === 0 &&
      donationStep !== DONATION_STEPS.BASKET_CREATION
    ) {
      setDonationStep(DONATION_STEPS.BASKET_CREATION);
    }
  }, [location.search]);

  return (
    <DonationContext.Provider
      value={{
        organizationId,
        donationStep,
        basket,
        setBasket,
        addDonationToBasket,
        increaseDonationCount,
        decreaseDonationCount,
        setDonationStep,
        isPackageDrawerOpen,
        setIsPackageDrawerOpen,
        isCartDrawerOpen,
        setIsCartDrawerOpen,
        isGiftAidDrawerOpen,
        setIsGiftAidDrawerOpen,
        isAuthModalOpen,
        setIsAuthModalOpen,
        authModalSource,
        setAuthModalSource,
        isDonorAuthenticated,
        setIsDonorAuthenticated,
        donationPackages,
        setDonationPackages,
        urgentAppeal,
        setUrgentAppeal,
        giftAid,
        setGiftAid,
        transactionFee,
        setTransactionFee,
        transactionFeeAmount,
        setTransactionFeeAmount,
        donor,
        setDonor,
        subtotal,
        totalAmount,
        removeDonation,
        setClientSecret,
        clientSecret,
        setStripeKey,
        stripeKey
      }}
    >
      {children}
    </DonationContext.Provider>
  );
}

DonationContextProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useDonationContextProvider = () => {
  const context = useContext(DonationContext);
  if (context === undefined) {
    throw new Error('Context Problem.');
  }
  return context;
};

export default DonationContext;
